import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, 
  Paper, Chip, CircularProgress, Alert
} from '@mui/material';
import { getModels } from '../../api/services/agent-studio/AgentFeatures';
import { DEMO_MODELS } from '../../helpers/MockData';
import { theme } from '../../helpers/theme';

const ModelsTab = ({ apiKey, endpoint, selectedModel }) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchModels = async () => {
      if (!apiKey || !endpoint) {
        setError('API key and endpoint are required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getModels(apiKey, endpoint);
        if (response.data) {
          const modelList = response.data.map(model => ({
            id: model.key,
            name: model.key.split('/').pop(),
            fullName: model.key,
            provider: model.key.split('/')[0],
            status: 'Available'
          }));
          setModels(modelList);
        }
      } catch (err) {
        console.error('Error fetching models:', err);
        setError(err.message || 'Failed to fetch models');
        setModels(DEMO_MODELS.map(model => ({
          ...model,
          id: model.value || model.id,
          fullName: model.value || model.id,
          provider: (model.value || model.id).split('/')[0],
          status: 'Demo'
        })));
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, [apiKey, endpoint]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
        Available Models
      </Typography>
      
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}. Showing demo models instead.
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: theme.palette.background.paper }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Model Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Provider</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Full Path</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Current</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {models.map((model) => (
              <TableRow 
                key={model.id}
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.02)' },
                  backgroundColor: selectedModel === model.fullName ? 'rgba(64, 196, 99, 0.1)' : 'transparent'
                }}
              >
                <TableCell sx={{ color: 'white' }}>{model.name}</TableCell>
                <TableCell>
                  <Chip 
                    label={model.provider} 
                    size="small"
                    sx={{ 
                      backgroundColor: theme.palette.primary.main, 
                      color: 'black',
                      fontWeight: 500
                    }}
                  />
                </TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {model.fullName}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={model.status}
                    size="small"
                    sx={{ 
                      backgroundColor: model.status === 'Available' ? 'rgba(76, 175, 80, 0.8)' : 'rgba(255, 152, 0, 0.8)',
                      color: 'white',
                      fontWeight: 500
                    }}
                  />
                </TableCell>
                <TableCell>
                  {selectedModel === model.fullName && (
                    <Chip 
                      label="ACTIVE"
                      size="small"
                      sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        color: 'black',
                        fontWeight: 600
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ModelsTab; 